/**
 * Event Walk-in Panel — operator registers a walk-in (+ guests) from the event page, on a phone,
 * at the event. Reuses the tested walk-in engine (lookupPerson / registerWalkIn → the
 * event-walkin-register edge fn). For a configured ticket event the walk-in check-in already
 * issues the reward (member award to the member; guest award to a non-member guest via the
 * escalation override), so "issue the ratchet/upgrade offer" happens inline — this panel surfaces
 * it. Mobile-first: full-screen on small screens, large touch targets, one sticky action button.
 */
import React, { useRef, useState } from "react";
import {
  lookupPerson,
  registerWalkIn,
  searchEventActiveMembers,
  type EventActiveMember,
  type PersonLookup,
  type WalkInResult,
  type WalkInRewardState,
} from "../../services/eventFieldCheckin.service";
import SearchBar from "../common/SearchBar";
import { SmsDisclosureText } from "../common/SmsDisclosureText";
import { SMS_DISCLOSURE_VERSIONS } from "../../constants/smsConsentDisclosures";

/**
 * F2: render the CONFIRMED reward, not "a ticket was minted". 'assigned' is the only state
 * that means the member/guest award actually landed; 'pending' is an un-escalated
 * placeholder and must read as not-yet-a-reward, never as a success.
 */
function RewardBadge({
  state,
  offer,
}: {
  state?: WalkInRewardState;
  offer?: string | null;
}) {
  if (state === "assigned") {
    return (
      <span className="text-green-800">
        · reward issued{offer ? `: ${offer}` : ""}
      </span>
    );
  }
  if (state === "pending") {
    return (
      <span className="text-amber-700">
        · reward pending — not yet finalized
      </span>
    );
  }
  if (state === "void") {
    return <span className="text-red-700">· reward voided</span>;
  }
  return <span className="text-gray-500">· no reward</span>;
}

interface Guest {
  firstName: string;
  lastName: string;
  email: string;
  issueVoucher: boolean;
}

interface Props {
  eventId: string;
  eventTitle: string;
  onClose: () => void;
  onRegistered?: () => void;
}

const inputCls =
  "w-full rounded-lg border border-gray-300 px-3 py-3 text-base focus:border-blue-500 focus:outline-none";

const EventWalkinPanel: React.FC<Props> = ({
  eventId,
  eventTitle,
  onClose,
  onRegistered,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [memberId, setMemberId] = useState("");
  const [phone, setPhone] = useState("");
  const [zip, setZip] = useState("");
  // Batch #3: a non-member/guest walk-in must provide a phone AND check the required SMS opt-in.
  const [smsConsent, setSmsConsent] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [issueVoucher, setIssueVoucher] = useState(true);
  const [lookup, setLookup] = useState<PersonLookup | null>(null);
  const [looking, setLooking] = useState(false);

  const [guests, setGuests] = useState<Guest[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<WalkInResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // F2: when the typed email is on file for a blank/different-named contact, the operator confirms at the
  // booth whether it's the same person before we reuse it.
  const [pendingConfirm, setPendingConfirm] = useState<{
    onFileName: string | null;
    ownerContactId: string | null;
  } | null>(null);
  const [override, setOverride] = useState(false);

  // ---- "Find an active member" search picker (explicit-submit only). ----
  const [searchResults, setSearchResults] = useState<
    EventActiveMember[] | null
  >(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] =
    useState<EventActiveMember | null>(null);
  // Request-generation guard: only the newest submitted search may write results (protects against a
  // slow earlier response landing after a newer one).
  const searchGen = useRef(0);
  // Same guard for the automatic email/member-# lookup: a slow earlier response must not overwrite a
  // newer one, and any keystroke edit (onChange below bumps this) invalidates an in-flight lookup.
  const lookupGen = useRef(0);
  // Name to show on the success screen (the selected member's, or the typed registrant's).
  const [successName, setSuccessName] = useState("");

  const rewardLabel = lookup?.hasTicket ? "ticket" : "reward";
  // Business rule (2026-08-20): guests and non-members are NOT ratcheted. Once a
  // lookup confirms the registrant is NOT an active member, the reward/upgrade
  // offer is greyed out and never issued for them.
  const ratchetBlocked = !!lookup?.found && !lookup.isMember;
  // Batch #3: require phone + SMS opt-in for the manual walk-in UNLESS the lookup has confirmed the
  // registrant is an active member (members/primary don't re-opt-in). No lookup yet ⇒ treat as a
  // non-member/guest and require it. The selected-active-member path (submitSelected) is exempt.
  const registrantConfirmedMember = !!lookup?.found && !!lookup.isMember;
  const requireSmsConsent = !registrantConfirmedMember;

  // `v` lets a caller look up values it has just set (state isn't updated yet in this render).
  async function runLookup(v?: {
    email: string;
    memberId: string;
    firstName: string;
    lastName: string;
  }) {
    const cur = v ?? { email, memberId, firstName, lastName };
    if (!cur.email.trim() && !cur.memberId.trim()) {
      return;
    }
    const gen = ++lookupGen.current;
    setLooking(true);
    try {
      const r = await lookupPerson({
        eventId,
        email: cur.email,
        memberId: cur.memberId,
      });
      // Drop a stale/superseded response: a newer lookup or a keystroke edit started after us.
      if (gen !== lookupGen.current) {
        return;
      }
      setLookup(r);
      if (r.found) {
        if (r.firstName && !cur.firstName) {
          setFirstName(r.firstName);
        }
        if (r.lastName && !cur.lastName) {
          setLastName(r.lastName);
        }
        if (r.membershipId && !cur.memberId) {
          setMemberId(r.membershipId);
        }
      }
    } catch {
      // lookup is best-effort; the operator can still register
    } finally {
      if (gen === lookupGen.current) {
        setLooking(false);
      }
    }
  }

  function addGuest() {
    setGuests((g) => [
      ...g,
      { firstName: "", lastName: "", email: "", issueVoucher: false }, // guests never get a reward (business rule)
    ]);
  }
  function updateGuest(i: number, patch: Partial<Guest>) {
    setGuests((g) => g.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }
  function removeGuest(i: number) {
    setGuests((g) => g.filter((_, idx) => idx !== i));
  }
  // Staff sometimes type the person checking in as a "guest" with the attendee fields empty
  // (e.g. a member who shows as not active). Guests need an attendee, so offer to move them up.
  function moveGuestToAttendee(i: number) {
    const g = guests[i];
    const next = {
      email: g.email.trim(),
      memberId: "",
      firstName: g.firstName.trim(),
      lastName: g.lastName.trim(),
    };
    setFirstName(next.firstName);
    setLastName(next.lastName);
    setEmail(next.email);
    setMemberId("");
    setPendingConfirm(null);
    setLookup(null);
    removeGuest(i);
    void runLookup(next);
  }

  // The server hard-blocks the typed register path with a lookup_conflict 409 when the email and
  // member # resolve to different contacts. Mirror that in the UI: block the typed submit so the
  // operator resolves it via the member picker instead of firing a doomed request.
  const conflictBlocked = !!lookup?.conflict;
  const canSubmit =
    !!firstName.trim() &&
    !!lastName.trim() &&
    !!email.trim() &&
    !submitting &&
    !conflictBlocked;
  // What's still missing, so a greyed-out button explains itself.
  const missingAttendee = [
    !firstName.trim() && "first name",
    !lastName.trim() && "last name",
    !email.trim() && "email",
  ].filter(Boolean) as string[];
  const attendeeEmpty =
    !firstName.trim() && !lastName.trim() && !email.trim() && !memberId.trim();
  const guestAsAttendee = attendeeEmpty
    ? guests.findIndex(
        (g) => g.firstName.trim() || g.lastName.trim() || g.email.trim(),
      )
    : -1;

  // Shared handling for a registerWalkIn result (typed OR selected-member path) so there is one
  // success/error/capacity flow. `name` is what to show on the success screen.
  function handleWalkInResult(r: WalkInResult, name: string) {
    if (r.lookupConflict) {
      setError(
        r.message ||
          "Email and member ID match different people. Correct one and retry.",
      );
    } else if (r.emailNeedsConfirmation) {
      setPendingConfirm({
        onFileName: r.onFileName ?? null,
        ownerContactId: r.ownerContactId ?? null,
      });
    } else if (r.capacityBlocked) {
      setOverride(true);
      setError(
        `This event is at capacity (${r.usedPeople}/${r.maxCapacity}). You can override to check them in anyway.`,
      );
    } else if (!r.ok) {
      setError(r.message || r.error || "Could not register the walk-in.");
    } else {
      setSuccessName(name);
      setResult(r);
      onRegistered?.();
    }
  }

  // ---- Member search (fires ONLY from SearchBar submit/Enter or Clear — never on keystroke). ----
  async function runMemberSearch(query: string) {
    setSearchError(null);
    // A new or cleared search deliberately drops any prior selection.
    setSelectedMember(null);
    const q = query.trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    const gen = ++searchGen.current;
    setSearching(true);
    try {
      const res = await searchEventActiveMembers(eventId, q);
      if (gen !== searchGen.current) {
        return; // a newer search superseded this one — ignore the stale response
      }
      if (!res.ok) {
        setSearchError(
          res.message || "Couldn't run the search. Please try again.",
        );
        setSearchResults([]);
      } else {
        setSearchResults(res.members);
      }
    } catch {
      if (gen !== searchGen.current) {
        return;
      }
      setSearchError("Couldn't run the search right now. Please try again.");
      setSearchResults([]);
    } finally {
      if (gen === searchGen.current) {
        setSearching(false);
      }
    }
  }

  function selectMember(m: EventActiveMember) {
    setSelectedMember(m);
    setSearchResults(null); // collapse the result list; the selected card takes its place
    setError(null);
    setPendingConfirm(null);
    setOverride(false);
  }

  function useDifferentMember() {
    setSelectedMember(null);
    setError(null);
    setOverride(false);
  }

  function useManualInstead() {
    setSelectedMember(null);
    setSearchResults(null);
    setSearchError(null);
    setError(null);
    setOverride(false);
  }

  async function submit(withOverride: boolean, confirmEmailOwnerId?: string) {
    setError(null);
    setPendingConfirm(null);
    // Batch #3: for a non-member/guest walk-in, phone is required and the SMS opt-in must be checked.
    if (requireSmsConsent) {
      const p = phone.trim();
      if (!p || (p.match(/\d/g) || []).length < 10) {
        setConsentError(
          "A valid phone number is required for a guest / non-member.",
        );
        return;
      }
      if (!smsConsent) {
        setConsentError("Please check the SMS opt-in box to continue.");
        return;
      }
    }
    setConsentError(null);
    setSubmitting(true);
    try {
      const capturedGuests = guests
        .filter((g) => g.email.trim())
        .map((g) => ({
          firstName: g.firstName.trim(),
          lastName: g.lastName.trim(),
          email: g.email.trim(),
          // Guests are never issued a reward/offer (business rule 2026-08-20).
          issueVoucher: false,
        }));
      const r = await registerWalkIn({
        eventId,
        mode: "checkin",
        registrant: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          memberId: memberId.trim() || undefined,
          phone: phone.trim() || undefined,
          zip: zip.trim() || undefined,
        },
        // Never ratchet a confirmed non-member (business rule).
        issueVoucher: issueVoucher && !ratchetBlocked,
        // Batch #3: forward the non-member/guest's affirmative SMS opt-in (server records it on the
        // registrant contact, and only when they are not a member).
        messagingConsent: requireSmsConsent && smsConsent,
        capturedGuests,
        override: withOverride,
        confirmEmailOwnerId,
        // Option A: this event-page panel issues ONLY the event's configured ticket
        // reward and confirms the escalated award — never unrelated active offers.
        configuredTicketOnly: true,
      });
      handleWalkInResult(r, `${firstName.trim()} ${lastName.trim()}`.trim());
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Check in the SELECTED active member via the server-authoritative selected path. Typed fields are NOT
  // sent as identity — the server re-reads selectedContactId and ignores anything typed, so a stale card
  // can't redirect the check-in.
  async function submitSelected(withOverride: boolean) {
    if (!selectedMember || submitting) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const r = await registerWalkIn({
        eventId,
        mode: "checkin",
        selectedContactId: selectedMember.contact_id,
        registrant: {
          // Echoed from the selected card for completeness only; the edge fn uses the server re-read.
          firstName: selectedMember.first_name ?? "",
          lastName: selectedMember.last_name ?? "",
          email: selectedMember.email ?? "",
          memberId: selectedMember.membership_id ?? undefined,
          phone: selectedMember.phone ?? undefined,
        },
        // A selected result is an active member, so their reward/upgrade is eligible.
        issueVoucher,
        override: withOverride,
        configuredTicketOnly: true,
      });
      handleWalkInResult(
        r,
        `${selectedMember.first_name ?? ""} ${selectedMember.last_name ?? ""}`.trim() ||
          "Member",
      );
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Success screen ----
  if (result) {
    const memberTickets = result.tickets ?? [];
    return (
      <Shell title="Checked in" onClose={onClose}>
        <div className="space-y-4">
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <p className="font-semibold text-green-900">
              {(successName || `${firstName} ${lastName}`).trim()} is checked
              in.
            </p>
            <p className="text-sm text-green-800">
              {result.registrantIsMember
                ? result.memberVerifiedLive
                  ? "Member (confirmed with Zuora just now)"
                  : "Member"
                : "Guest / non-member"}{" "}
              <RewardBadge
                state={result.rewardState}
                offer={result.rewardOffer}
              />
            </p>
            {result.consentRecorded === false && (
              /* SMS consent audit (HIGH 2): the check-in went through but the opt-in was NOT
                 recorded. Say so plainly rather than letting the green panel imply consent was
                 captured — the operator ticked the box on the registrant's behalf. */
              <p className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                <span className="font-semibold">
                  Text-message opt-in was not recorded.
                </span>{" "}
                The check-in is complete, but this person is not opted in to
                texts. Ask them again at the booth, or report it — it has been
                logged for follow-up.
              </p>
            )}
            {memberTickets.length > 0 && (
              <div className="mt-3 space-y-3">
                {memberTickets.map((t) => (
                  <div key={t.code} className="text-center">
                    {t.qrUrl && (
                      <img
                        src={t.qrUrl}
                        alt="Ticket QR code"
                        className="mx-auto h-40 w-40 rounded-lg border border-green-200 bg-white object-contain"
                      />
                    )}
                    <p className="mt-1 font-mono text-sm font-semibold text-gray-800">
                      {t.code}
                    </p>
                  </div>
                ))}
                <p className="text-center text-xs text-green-800">
                  Show this to the attendee — they can take a picture of it.
                </p>
              </div>
            )}
          </div>

          {(result.warnings ?? []).length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
              <p className="mb-1 text-sm font-semibold text-amber-900">
                A couple of things to note
              </p>
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-amber-800">
                {(result.warnings ?? []).map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {(result.guests ?? []).length > 0 && (
            <div className="rounded-lg border border-gray-200 p-4">
              <p className="mb-2 text-sm font-semibold text-gray-900">Guests</p>
              {(result.guests ?? []).map((g) => (
                <div
                  key={g.email}
                  className="border-b border-gray-100 py-2 last:border-0"
                >
                  <p className="text-sm text-gray-900">{g.email}</p>
                  <p className="text-xs">
                    <RewardBadge state={g.rewardState} offer={g.rewardOffer} />
                    <span className="text-gray-500">
                      {(g.tickets ?? []).map((t) => ` · ${t.code}`).join("")}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-semibold text-white"
          >
            Done
          </button>
        </div>
      </Shell>
    );
  }

  // ---- Form ----
  return (
    <Shell title="Register walk-in" onClose={onClose}>
      <p className="mb-3 text-sm text-gray-500">{eventTitle}</p>

      {/* Find an active member — explicit-submit search picker. */}
      <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50/50 p-3">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            Find an active member
          </h3>
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-800">
            Active members only
          </span>
        </div>
        <p className="mb-2 text-xs text-gray-600">
          Search a current member to check in. For a guest, a former/cancelled
          member, or a non-member, use the manual fields below instead — an
          empty result here doesn’t mean the person isn’t in the system.
        </p>

        {!selectedMember && (
          <>
            <SearchBar
              onSearch={runMemberSearch}
              placeholder="Search name, email, phone, or member ID"
              minChars={2}
            />
            {searching && (
              <p className="mt-2 text-xs text-gray-500">Searching…</p>
            )}
            {searchError && (
              <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                {searchError}
              </div>
            )}
            {!searching &&
              !searchError &&
              searchResults &&
              searchResults.length === 0 && (
                <p className="mt-2 text-xs text-gray-500">
                  No active members matched. Check the spelling, or use the
                  manual fields below for a guest / former member / non-member.
                </p>
              )}
            {searchResults && searchResults.length > 0 && (
              <ul className="mt-2 space-y-2">
                {searchResults.map((m) => (
                  <li key={m.contact_id}>
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => selectMember(m)}
                      className="w-full rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-blue-400 disabled:opacity-50"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">
                          {[m.first_name, m.last_name]
                            .filter(Boolean)
                            .join(" ") || "(no name)"}
                        </span>
                        {m.is_primary_member === false && (
                          <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">
                            Household
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-gray-600">
                        {m.email || "No email on file"}
                        {m.membership_id ? ` · #${m.membership_id}` : ""}
                      </div>
                      <div className="text-xs text-gray-500">
                        {m.phone || "No phone"}
                        {m.postal_code ? ` · ${m.postal_code}` : ""}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {selectedMember && (
          <div className="rounded-lg border border-blue-300 bg-white p-3">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-gray-900">
                {[selectedMember.first_name, selectedMember.last_name]
                  .filter(Boolean)
                  .join(" ") || "(no name)"}
              </span>
              <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-800">
                Selected member
              </span>
            </div>
            <div className="mt-0.5 text-xs text-gray-600">
              {selectedMember.email || "No email on file"}
              {selectedMember.membership_id
                ? ` · #${selectedMember.membership_id}`
                : ""}
            </div>
            <div className="text-xs text-gray-500">
              {selectedMember.phone || "No phone"}
              {selectedMember.postal_code
                ? ` · ${selectedMember.postal_code}`
                : ""}
            </div>
            {!selectedMember.email && (
              <p className="mt-1 text-xs text-amber-700">
                No email on file — they’ll be checked in and shown their
                ticket/QR on screen, but it can’t be emailed.
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-4">
              <button
                type="button"
                onClick={useDifferentMember}
                className="text-sm font-medium text-blue-600"
              >
                Use a different member
              </button>
              <button
                type="button"
                onClick={useManualInstead}
                className="text-sm font-medium text-gray-600"
              >
                Use manual validation instead
              </button>
            </div>
          </div>
        )}
      </div>

      {!selectedMember && (
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Attendee checking in
            </p>
            <p className="text-xs text-gray-500">
              The person in front of you — member, former member or non-member.
            </p>
          </div>
          <input
            className={inputCls}
            type="email"
            inputMode="email"
            placeholder="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setPendingConfirm(null);
              // Editing the inputs invalidates any in-flight lookup and clears the (now stale)
              // member banner so it can't linger against the new value.
              lookupGen.current++;
              setLookup(null);
            }}
            onBlur={() => void runLookup()}
            autoComplete="off"
          />
          <input
            className={inputCls}
            placeholder="Member # (optional)"
            value={memberId}
            onChange={(e) => {
              setMemberId(e.target.value);
              setPendingConfirm(null);
              lookupGen.current++;
              setLookup(null);
            }}
            onBlur={() => void runLookup()}
            autoComplete="off"
          />
          {looking && <p className="text-xs text-gray-500">Checking…</p>}
          {conflictBlocked && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
              <p className="font-semibold">
                Email and member # don’t match the same person
              </p>
              <p className="mt-1">
                The email is on file for{" "}
                <span className="font-medium">
                  {lookup?.emailContact?.name || "one contact"}
                </span>
                , but the member # belongs to{" "}
                <span className="font-medium">
                  {lookup?.memberIdContact?.name || "a different contact"}
                </span>
                . Resolve this before checking in — use “Find an active member”
                above to pick the right person, or correct the email / member #.
              </p>
            </div>
          )}
          {lookup?.found && !conflictBlocked && (
            <div
              className={`rounded-md px-3 py-2 text-sm ${
                lookup.isMember
                  ? "bg-green-50 text-green-800"
                  : "bg-gray-100 text-gray-700"
              }`}
            >
              {lookup.isMember
                ? lookup.memberVerifiedLive
                  ? "Active member (confirmed with Zuora just now)"
                  : "Active member"
                : "Not a member"}
              {lookup.hasTicket ? " · this event issues admission tickets" : ""}
            </div>
          )}
          <div className="flex gap-3">
            <input
              className={inputCls}
              placeholder="First name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setPendingConfirm(null);
              }}
            />
            <input
              className={inputCls}
              placeholder="Last name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setPendingConfirm(null);
              }}
            />
          </div>
          <input
            className={inputCls}
            type="tel"
            inputMode="tel"
            placeholder={requireSmsConsent ? "Phone *" : "Phone (optional)"}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <input
            className={inputCls}
            inputMode="numeric"
            placeholder="ZIP (needed to confirm a member)"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            autoComplete="off"
          />

          <label
            className={`flex items-center gap-3 py-1 ${
              ratchetBlocked ? "opacity-50" : ""
            }`}
          >
            <input
              type="checkbox"
              className="h-5 w-5"
              disabled={ratchetBlocked}
              checked={issueVoucher && !ratchetBlocked}
              onChange={(e) => setIssueVoucher(e.target.checked)}
            />
            <span className="text-base text-gray-900">
              Issue their {rewardLabel} / upgrade offer
            </span>
          </label>
          {ratchetBlocked && (
            <p className="-mt-1 text-xs text-gray-500">
              Not an active member — non-members aren’t issued a reward or
              upgrade.
            </p>
          )}

          {/* Batch #3: required SMS opt-in for a guest / non-member walk-in. Hidden once the lookup
              confirms an active member (they don't re-opt-in). Wording is a placeholder for Legal. */}
          {requireSmsConsent && (
            <div className="rounded-lg border border-gray-200 p-3">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 shrink-0"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                />
                <span className="text-xs text-gray-600">
                  {/* Rendered from the canonical registry. This is the OPERATOR-witnessed version
                      (third person — the operator attests on the registrant's behalf); the server
                      freezes this exact wording into the evidence row. */}
                  <SmsDisclosureText
                    version={SMS_DISCLOSURE_VERSIONS.walkinOperator}
                  />{" "}
                  <span className="text-red-600">*</span>
                </span>
              </label>
            </div>
          )}
          {consentError && (
            <p className="-mt-1 text-xs text-red-600">{consentError}</p>
          )}

          {/* Guests */}
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                Guests they brought (optional)
              </span>
              <button
                type="button"
                onClick={addGuest}
                className="text-sm font-medium text-blue-600"
              >
                + Add guest
              </button>
            </div>
            {guests.length === 0 && (
              <p className="text-xs text-gray-500">
                Only for people who came with the attendee above — they’re
                recorded as attendees and aren’t issued a reward. To check in a
                former member or non-member on their own, use the attendee
                fields above.
              </p>
            )}
            {guests.map((g, i) => (
              <div
                key={i}
                className="mt-2 space-y-2 border-t border-gray-100 pt-2"
              >
                <div className="flex gap-2">
                  <input
                    className={inputCls}
                    placeholder="Guest first name"
                    value={g.firstName}
                    onChange={(e) =>
                      updateGuest(i, { firstName: e.target.value })
                    }
                  />
                  <input
                    className={inputCls}
                    placeholder="Guest last name"
                    value={g.lastName}
                    onChange={(e) =>
                      updateGuest(i, { lastName: e.target.value })
                    }
                  />
                </div>
                <input
                  className={inputCls}
                  type="email"
                  inputMode="email"
                  placeholder="Guest email"
                  value={g.email}
                  onChange={(e) => updateGuest(i, { email: e.target.value })}
                />
                {!g.email.trim() &&
                  (g.firstName.trim() || g.lastName.trim()) && (
                    <p className="text-xs text-amber-700">
                      A guest without an email isn’t recorded.
                    </p>
                  )}
                <div className="flex items-center justify-end">
                  {/* "Issue guest offer" removed — guests/non-members are not
                    ratcheted (business rule 2026-08-20). */}
                  <button
                    type="button"
                    onClick={() => removeGuest(i)}
                    className="text-sm text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            {/* A non-member may bring guests (owner 2026-10-23). A guest gives only name + email,
                so nudge — never block — toward a separate check-in that captures phone + opt-in. */}
            {guests.length > 0 && requireSmsConsent && guestAsAttendee < 0 && (
              <p
                className="mt-2 text-xs text-gray-500"
                data-testid="walkin-guest-separate-note"
              >
                Checking a guest in separately as an attendee also captures
                their phone and text opt-in.
              </p>
            )}
          </div>
          {guestAsAttendee >= 0 && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="walkin-guest-as-attendee"
            >
              <p>
                Is this guest the person checking in? A guest has to come with
                an attendee — fill in the attendee fields above.
              </p>
              <button
                type="button"
                onClick={() => moveGuestToAttendee(guestAsAttendee)}
                className="mt-2 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-sm font-semibold text-amber-900"
              >
                Move to attendee
              </button>
            </div>
          )}
        </div>
      )}

      {/* Reward toggle for a SELECTED active member (the manual block's toggle is hidden then). */}
      {selectedMember && (
        <label className="mt-1 flex items-center gap-3 py-1">
          <input
            type="checkbox"
            className="h-5 w-5"
            checked={issueVoucher}
            onChange={(e) => setIssueVoucher(e.target.checked)}
          />
          <span className="text-base text-gray-900">
            Issue their reward / upgrade offer
          </span>
        </label>
      )}

      {error && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {error}
        </div>
      )}

      {pendingConfirm && (
        <div className="mt-3 rounded-md border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900">
          <p className="mb-2">
            That email is already on file for{" "}
            <span className="font-semibold">
              {pendingConfirm.onFileName || "a contact with no name recorded"}
            </span>
            . Is this the same person?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={submitting}
              onClick={() =>
                submit(override, pendingConfirm.ownerContactId ?? undefined)
              }
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {submitting ? "Checking in…" : "Yes, check them in"}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => {
                setPendingConfirm(null);
                setError(
                  "Use a different email for this person, or find and check in their existing contact.",
                );
              }}
              className="rounded-md border border-blue-300 bg-white px-3 py-1.5 text-sm font-semibold text-blue-800 disabled:opacity-50"
            >
              No, different person
            </button>
          </div>
        </div>
      )}

      {/* Sticky action */}
      <div className="sticky bottom-0 -mx-4 mt-4 border-t border-gray-200 bg-white px-4 py-3">
        {selectedMember ? (
          override ? (
            <button
              type="button"
              disabled={submitting}
              onClick={() => submitSelected(true)}
              className="w-full rounded-lg bg-amber-600 px-4 py-3 text-base font-semibold text-white disabled:opacity-50"
            >
              {submitting ? "Checking in…" : "Override capacity & check in"}
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting}
              onClick={() => submitSelected(false)}
              className="w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-semibold text-white disabled:opacity-50"
            >
              {submitting ? "Checking in…" : "Check in this member"}
            </button>
          )
        ) : override ? (
          <button
            type="button"
            disabled={submitting || conflictBlocked}
            onClick={() => submit(true)}
            className="w-full rounded-lg bg-amber-600 px-4 py-3 text-base font-semibold text-white disabled:opacity-50"
          >
            {submitting ? "Registering…" : "Override capacity & check in"}
          </button>
        ) : (
          <>
            {!submitting && !conflictBlocked && missingAttendee.length > 0 && (
              <p
                className="mb-2 text-xs text-gray-600"
                id="walkin-submit-hint"
                data-testid="walkin-submit-hint"
                aria-live="polite"
              >
                {guestAsAttendee >= 0
                  ? "Only a guest is filled in. If they’re the person checking in, tap “Move to attendee” below the guests."
                  : `Enter the attendee’s ${missingAttendee.join(", ")} above to continue.`}
              </p>
            )}
            <button
              type="button"
              disabled={!canSubmit}
              aria-describedby={
                missingAttendee.length > 0 ? "walkin-submit-hint" : undefined
              }
              onClick={() => submit(false)}
              className="w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-semibold text-white disabled:opacity-50"
            >
              {submitting ? "Registering…" : "Register & check in"}
            </button>
          </>
        )}
      </div>
    </Shell>
  );
};

/** Full-screen on phones, centered card on larger screens. */
const Shell: React.FC<{
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 sm:items-center sm:p-4">
    <div className="flex w-full max-w-md flex-col bg-white sm:max-h-[90vh] sm:rounded-xl sm:shadow-xl">
      <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-md p-1 text-2xl leading-none text-gray-500"
        >
          ×
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
    </div>
  </div>
);

export default EventWalkinPanel;
